import React, { useLayoutEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import Dropdown from '../../common/Dropdown'
import { loadAdminStoreProductCategories } from '../../../actions/adminStore'
import { loadServices } from '../../../actions/services'
import { useWindowSize } from '../../../libs/hooks'
import { capitalizeFirstLetter } from '../../../utils/functions'

const hasEnabledDailyPlans = process.env.REACT_APP_ONE_DAY_SUBSCRIPTION_TOGGLE?.toLowerCase() === 'on'

const FormProductDetails = (props) => {
  const { t } = useTranslation()
  const isMobile = useWindowSize()[0] <= 768
  const { productDetailsFormData, productDetailsErrors, isEditMode, updateProductDetails, productCategories, services, pricePlans } = props
  const [productCategoryOptions, setProductCategoryOptions] = useState([])
  const [serviceOptions, setServiceOptions] = useState([])
  const isDigitalProductOptions = [t('no'), t('yes')]
  const filteredPricePlans = pricePlans.filter((p) => hasEnabledDailyPlans || p.recurrenceInterval !== 'day')

  useLayoutEffect(() => {
    if (props.productCategories.length === 0) {
      props.actions.loadAdminStoreProductCategories()
    }

    if (props.services.length === 0) {
      props.actions.loadServices({ isEnabled: true }, false, true)
    }
  }, [props.actions])

  useLayoutEffect(() => {
    let categoryId = productDetailsFormData.categoryId
    let serviceId = productDetailsFormData.serviceId

    const categories = props.productCategories
      .filter((category) => productDetailsFormData.isDigital === category.isDigital)
      .map((category) => {
        return { id: category.id, name: category.name }
      })

    setProductCategoryOptions(categories)

    if (categories.length > 0) {
      categoryId = categoryId ? categoryId : categories[0].id
    }

    if (productDetailsFormData.isDigital) {
      const services = props.services.map((service) => {
        return { id: service.id, name: service.name }
      })

      setServiceOptions(services)

      if (services.length > 0) {
        serviceId = serviceId ? serviceId : services[0].id
      }
    }

    updateProductDetails({
      ...productDetailsFormData,
      categoryId: categoryId,
      serviceId: serviceId,
    })
  }, [props.productCategories, props.services, productDetailsFormData.isDigital])

  const selectIsDigitalProduct = (isDigitalProduct) => {
    if (
      (isDigitalProduct === isDigitalProductOptions[1] && productDetailsFormData.isDigital) ||
      (isDigitalProduct === isDigitalProductOptions[0] && !productDetailsFormData.isDigital)
    ) {
      return
    }

    updateProductDetails({
      ...productDetailsFormData,
      isDigital: isDigitalProduct === isDigitalProductOptions[1],
      categoryId: null,
      serviceId: null,
      planType: null,
    })
  }

  const selectCategory = (categoryId) => {
    updateProductDetails({ ...productDetailsFormData, categoryId: categoryId })
  }

  const selectService = (serviceId) => {
    updateProductDetails({ ...productDetailsFormData, serviceId: serviceId })
  }

  const selectPlanType = (planType) => {
    updateProductDetails({ ...productDetailsFormData, planType: planType })
  }

  return (
    <div className="product-details-wrapper">
      <div className="fixed-input-wrapper one-third-wrapper">
        <div className="input-label">
          {capitalizeFirstLetter(t('part_number'))}
          <span className="required-mark">*</span>
        </div>
        {!isEditMode ? (
          <input
            type="text"
            className={'input-item' + (productDetailsErrors?.partNumber ? ' input-error' : '')}
            value={productDetailsFormData.partNumber}
            onChange={(e) => updateProductDetails({ ...productDetailsFormData, partNumber: e.target.value })}
          />
        ) : (
          <input type="text" className="input-item" value={productDetailsFormData.partNumber} disabled />
        )}
      </div>
      <div className="fixed-input-wrapper two-third-wrapper">
        <div className="input-label">
          {t('coltene_store.create_product.product-main-title')}
          <span className="required-mark">*</span>
        </div>
        <input
          type="text"
          className={'input-item' + (productDetailsErrors?.productMainTitle ? ' input-error' : '')}
          value={productDetailsFormData.productMainTitle}
          onChange={(e) => updateProductDetails({ ...productDetailsFormData, productMainTitle: e.target.value })}
        />
      </div>
      <div className="fixed-input-wrapper two-third-wrapper">
        <div className="input-label">
          {t('coltene_store.create_product.subtitle')}
          <span className="required-mark">*</span>
        </div>
        <input
          type="text"
          className={'input-item' + (productDetailsErrors?.productSubtitle ? ' input-error' : '')}
          value={productDetailsFormData.productSubtitle}
          onChange={(e) => updateProductDetails({ ...productDetailsFormData, productSubtitle: e.target.value })}
        />
      </div>
      <div className={`product-options-wrapper ${isMobile && 'flex-column'}`}>
        <div className="fixed-input-wrapper one-third-wrapper">
          <div className="input-label">
            {t('coltene_store.create_product.digital-product')}
            <span className="required-mark">*</span>
          </div>
          {!isEditMode ? (
            <Dropdown
              options={isDigitalProductOptions.map((option) => ({ key: option, value: option }))}
              initialSelectedKey={productDetailsFormData.isDigital ? isDigitalProductOptions[1] : isDigitalProductOptions[0]}
              onChange={(option) => selectIsDigitalProduct(option.key)}
              hasError={productDetailsErrors?.isDigital}
            />
          ) : (
            <input className="input-item" value={productDetailsFormData.isDigital ? t('yes') : t('no')} disabled />
          )}
        </div>
        <div className="fixed-input-wrapper one-third-wrapper">
          <div className="input-label">
            {t('category')}
            <span className="required-mark">*</span>
          </div>
          {!isEditMode ? (
            <Dropdown
              placeholder={t('coltene_store.create_product.select_option')}
              options={productCategoryOptions.map((category) => ({ key: category.id, value: category.name }))}
              initialSelectedKey={productDetailsFormData.categoryId}
              onChange={(category) => selectCategory(category.key)}
              hasError={productDetailsErrors?.categoryId}
            />
          ) : (
            <input
              className="input-item"
              value={productDetailsFormData.categoryId ? productCategories.find((e) => e.id === productDetailsFormData.categoryId)?.name || '' : ''}
              disabled
            />
          )}
        </div>
        {productDetailsFormData.isDigital && (
          <div className="fixed-input-wrapper one-third-wrapper">
            <div className="input-label">
              {t('coltene_store.create_product.service')}
              <span className="required-mark">*</span>
            </div>
            {!isEditMode ? (
              <Dropdown
                placeholder={t('coltene_store.create_product.select_option')}
                options={serviceOptions.map((service) => ({ key: service.id, value: service.name }))}
                initialSelectedKey={productDetailsFormData.serviceId}
                onChange={(service) => selectService(service.key)}
                hasError={productDetailsErrors?.serviceId}
              />
            ) : (
              <input
                className="input-item"
                value={productDetailsFormData.serviceId ? services.find((e) => e.id === productDetailsFormData.serviceId)?.name || '' : ''}
                disabled
              />
            )}
          </div>
        )}
      </div>
      {productDetailsFormData.isDigital && (
        <div className="fixed-input-wrapper one-third-wrapper">
          <div className="input-label">
            {t('coltene_store.subscription_history.planType')}
            <span className="required-mark">*</span>
          </div>
          {!isEditMode ? (
            <Dropdown
              placeholder={t('coltene_store.create_product.select_option')}
              options={filteredPricePlans.map((pricePlan) => ({ key: pricePlan.recurrenceInterval, value: pricePlan.title }))}
              initialSelectedKey={productDetailsFormData.planType}
              onChange={(planType) => selectPlanType(planType.key)}
              hasError={productDetailsErrors?.planType}
            />
          ) : (
            <input
              className="input-item"
              value={
                productDetailsFormData.planType ? filteredPricePlans.find((e) => e.recurrenceInterval === productDetailsFormData.planType)?.title || '' : ''
              }
              disabled
            />
          )}
        </div>
      )}
    </div>
  )
}

function stateToProps({ adminStore, services }) {
  return {
    productCategories: adminStore?.productCategories || [],
    services: services?.services || [],
    pricePlans: adminStore?.pricePlans || [],
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadAdminStoreProductCategories,
        loadServices,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(FormProductDetails)
